/**
 * Module #34 Phase A chunk 7e-b — daily insight digest settings control.
 *
 * A small owner-facing control on the Brief page: turn the daily digest email
 * on/off, pick the delivery hour, and pick the timezone that hour is in. The
 * timezone DEFAULTS to the owner's detected location (so "8:00 AM" means 8 AM
 * where they are) but is user-selectable, and it is NOT silently re-detected on
 * later saves — once chosen, the tz sticks. Stored alongside the hour so the
 * cron is DST-correct.
 *
 * Reads/writes tenants.metadata.insight_digest via the get/set RPCs (owner-only,
 * SECURITY DEFINER). Renders nothing for non-owners (the get RPC returns null).
 */

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../Toast';

interface DigestSettingsState {
  enabled: boolean;
  hour: number;
  timezone: string;
}

/** "8:00 AM" / "12:00 PM" etc. from a 0-23 hour. */
function hourLabel(h: number): string {
  const period = h < 12 ? 'AM' : 'PM';
  const display = h % 12 === 0 ? 12 : h % 12;
  return `${display}:00 ${period}`;
}

/** The owner's current timezone (IANA), used as the DEFAULT when none is stored. */
const BROWSER_TZ = ((): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
  } catch {
    return 'America/New_York';
  }
})();

/** Every IANA timezone, for the user-selectable picker (falls back to the detected tz). */
const ALL_TIMEZONES: readonly string[] = ((): readonly string[] => {
  try {
    return Intl.supportedValuesOf('timeZone');
  } catch {
    return [BROWSER_TZ];
  }
})();

export function DigestSettings() {
  const toast = useToast();
  const [settings, setSettings] = useState<DigestSettingsState | null>(null);
  const [available, setAvailable] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data, error: err } = await supabase.rpc('get_insight_digest_settings');
      if (cancelled) return;
      if (err || data === null || typeof data !== 'object') {
        setAvailable(false);
        return;
      }
      const d = data as Record<string, unknown>;
      setSettings({
        enabled: d['enabled'] !== false,
        hour: typeof d['hour'] === 'number' ? d['hour'] : 8,
        timezone: typeof d['timezone'] === 'string' ? d['timezone'] : BROWSER_TZ,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const save = useCallback(
    async (next: DigestSettingsState): Promise<void> => {
      setSaving(true);
      setError(null);
      setSettings(next); // optimistic
      const { error: err } = await supabase.rpc('set_insight_digest_settings', {
        p_enabled: next.enabled,
        p_hour: next.hour,
        p_timezone: next.timezone,
      });
      setSaving(false);
      if (err) {
        setError("Couldn't save — try again.");
        return;
      }
      toast.success('Saved.');
    },
    [toast],
  );

  if (!available || settings === null) return null;

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '0.5rem',
        fontSize: '0.875rem',
        color: 'var(--text)',
        background: 'var(--surface-elev)',
        border: '1px solid var(--border)',
        borderRadius: '0.5rem',
        padding: '0.625rem 0.875rem',
        marginBottom: '1rem',
      }}
    >
      <label
        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}
      >
        <input
          type="checkbox"
          checked={settings.enabled}
          onChange={(e) => void save({ ...settings, enabled: e.target.checked })}
        />
        Email me this brief daily
      </label>
      {settings.enabled && (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.4rem',
            flexWrap: 'wrap',
          }}
        >
          at
          <select
            value={settings.hour}
            onChange={(e) => void save({ ...settings, hour: Number(e.target.value) })}
            style={{
              border: '1px solid var(--border)',
              borderRadius: '0.375rem',
              padding: '0.2rem 0.4rem',
              fontSize: '0.875rem',
              background: 'var(--surface)',
              color: 'var(--text)',
            }}
          >
            {Array.from({ length: 24 }, (_, h) => h).map((h) => (
              <option key={h} value={h}>
                {hourLabel(h)}
              </option>
            ))}
          </select>
          <select
            value={settings.timezone}
            onChange={(e) => void save({ ...settings, timezone: e.target.value })}
            aria-label="Delivery timezone"
            title="The timezone your delivery time is in — defaults to your location"
            style={{
              border: '1px solid var(--border)',
              borderRadius: '0.375rem',
              padding: '0.2rem 0.4rem',
              fontSize: '0.8125rem',
              background: 'var(--surface)',
              color: 'var(--text-muted)',
              maxWidth: '13rem',
            }}
          >
            {(ALL_TIMEZONES.includes(settings.timezone)
              ? ALL_TIMEZONES
              : [settings.timezone, ...ALL_TIMEZONES]
            ).map((tz) => (
              <option key={tz} value={tz}>
                {tz.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </span>
      )}
      {saving && <span style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>Saving…</span>}
      {error !== null && (
        <span style={{ color: 'var(--danger, #dc2626)', fontSize: '0.8125rem' }}>{error}</span>
      )}
    </div>
  );
}
